/* eslint-disable no-console */
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Header,
  Icon,
  Button,
  Tab,
  Loader,
  Segment,
  Dropdown,
} from "semantic-ui-react";
import { CustomTable } from "../../../components/Shared";
import { AcademicCycle, Grade, Student } from "../../../api";
import { useAuth } from "../../../hooks";
import moment from "moment";
import "./Attendance.scss";

const academicCycleController = new AcademicCycle();
const gradeController = new Grade();
const studentController = new Student();

export function AttendanceByClass() {
  const [academicCycle, setAcademicCycle] = useState(null);
  const [grades, setGrades] = useState([]);
  const [students, setStudents] = useState({});
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    moment.utc().local().format("YYYY-MM-DD"),
  );
  const [loading, setLoading] = useState(true);
  const { accessToken } = useAuth();
  const { academicCycleId } = useParams();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        // Obtener información del ciclo académico
        const cycleResponse = await academicCycleController.getAcademicCycle(
          academicCycleId
        );
        setAcademicCycle(cycleResponse);

        // Obtener grados del nivel del ciclo académico
        const gradesResponse = await gradeController.getGradesByLevelId(
          cycleResponse.levelId,
          accessToken,
        );
        setGrades(gradesResponse);

        // Obtener estudiantes por grado
        const studentsData = {};
        for (const grade of gradesResponse) {
          try {
            const studentsResponse = await studentController.getStudents(
              accessToken,
              grade.id,
              academicCycleId,
              {},
              1,
              1000,
            );
            // La API devuelve { data: [...], pagination: {...} }
            studentsData[grade.id] = studentsResponse.data || [];
          } catch (error) {
            console.error(
              `Error fetching students for grade ${grade.id}:`,
              error,
            );
            studentsData[grade.id] = [];
          }
        }
        setStudents(studentsData);
      } catch (error) {
        console.error("Error loading attendance data:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, [academicCycleId, accessToken]);

  const subjectOptions = [
    { key: 1, text: "Matemáticas", value: 1 },
    { key: 2, text: "Comunicación", value: 2 },
    { key: 3, text: "Ciencias", value: 3 },
    { key: 4, text: "Historia", value: 4 },
    { key: 5, text: "Educación Física", value: 5 },
  ];

  const getTableConfig = () => ({
    headers: [
      "DNI",
      "Estudiante",
      "Correo electrónico",
      "Presente",
      "Tardanza",
      "Falta",
      "Observaciones",
    ],
    fields: [
      {
        field: "User.dni",
        dataType: "text",
      },
      {
        component: "custom",
        render: (student) => (
          <span>
            {student.User.lastname} {student.User.secondLastname}{" "}
            {student.User.firstname}
          </span>
        ),
      },
      {
        field: "User.email",
        dataType: "text",
      },
      {
        component: "custom",
        align: "center",
        render: () => <Button size="small" color="green" icon="check" basic />,
      },
      {
        component: "custom",
        align: "center",
        render: () => <Button size="small" color="yellow" icon="clock" basic />,
      },
      {
        component: "custom",
        align: "center",
        render: () => <Button size="small" color="red" icon="times" basic />,
      },
      {
        component: "custom",
        render: () => (
          <input
            type="text"
            placeholder="Observaciones..."
            style={{
              width: "100%",
              border: "1px solid #ddd",
              padding: "5px",
              borderRadius: "3px",
            }}
          />
        ),
      },
    ],
  });

  const renderStudentsAttendanceTable = (gradeId) => {
    const gradeStudents = students[gradeId] || [];

    if (gradeStudents.length === 0) {
      return (
        <Segment placeholder>
          <div className="attendance-by-class-page__empty">
            <Icon name="users" size="huge" />
            <p>No hay estudiantes registrados en este grado</p>
          </div>
        </Segment>
      );
    }

    return (
      <CustomTable
        tableConfig={getTableConfig()}
        data={gradeStudents}
        loading={false}
        striped={true}
        size="small"
      />
    );
  };

  const renderClassControls = () => (
    <div
      style={{
        marginBottom: "20px",
        padding: "15px",
        backgroundColor: "#f8f9fa",
        borderRadius: "5px",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: "15px",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div>
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              fontWeight: "bold",
            }}
          >
            Materia:
          </label>
          <Dropdown
            className="edunova-select"
            placeholder="Seleccionar materia"
            fluid
            selection
            options={subjectOptions}
            value={selectedSubject}
            onChange={(e, { value }) => setSelectedSubject(value)}
            style={{ minWidth: "200px" }}
          />
        </div>
        <div>
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              fontWeight: "bold",
            }}
          >
            Fecha:
          </label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={{
              padding: "8px",
              border: "1px solid #ddd",
              borderRadius: "4px",
            }}
          />
        </div>
        <div style={{ marginTop: "20px" }}>
          <Button color="blue" icon labelPosition="left">
            <Icon name="search" />
            Cargar Asistencia
          </Button>
        </div>
      </div>
    </div>
  );

  const panes = grades.map((grade) => ({
    menuItem: grade?.name,
    render: () => (
      <Tab.Pane attached={false}>
        {renderClassControls()}
        <div style={{ marginBottom: "1em" }}>
          <Button primary icon labelPosition="left" disabled={!selectedSubject}>
            <Icon name="save" />
            Guardar Asistencia - {grade?.name}
          </Button>
          <Button color="green" icon labelPosition="left">
            <Icon name="download" />
            Exportar Registro
          </Button>
        </div>
        {renderStudentsAttendanceTable(grade.id)}
      </Tab.Pane>
    ),
  }));

  if (loading) return <Loader active inline="centered" />;
  if (!academicCycle)
    return <div>Error al cargar la información del ciclo académico</div>;

  return (
    <div className="attendance-page">
      <Header as="h3">
        <Icon name="calendar check" />
        <Header.Content>
          Asistencia por Clase - {academicCycle?.name}
        </Header.Content>
        <Header.Subheader>
          {academicCycle.Level?.name} |{" "}
          {moment.utc(selectedDate).local().format("DD/MM/YYYY")}
        </Header.Subheader>
      </Header>

      {panes.length > 0 ? (
        <Tab
          className="attendance-page__filters"
          menu={{ secondary: true }}
          panes={panes}
        />
      ) : (
        <Segment placeholder>
          <Header icon>
            <Icon name="users" />
            No hay grados disponibles para este ciclo académico
          </Header>
        </Segment>
      )}
    </div>
  );
}
